import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import BlacksmithWorkshop from '../components/BlacksmithWorkshop';
import ConnectWalletModal from '../components/ConnectWalletModal';
import { createProfile, getProfile, updateProfile, setOisyWallet } from '../services/kip';
import { Principal } from '@dfinity/principal';
import { useAuth } from '../auth/useAuth';

export default function ProfilePage() {
  const { agent, isAuthenticated, principal } = useAuth();
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [preferences, setPreferences] = useState('');
  const [oisyWallet, setOisyWalletInput] = useState('');
  const [payoutCurrency, setPayoutCurrency] = useState('ICP');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);

  useEffect(() => {
    if (!agent || !isAuthenticated || !principal) return;
    loadProfile();
  }, [agent, isAuthenticated, principal]);

  const loadProfile = async () => {
    if (!agent || !principal) return;
    try {
      const profile = await getProfile(agent, principal);
      if (profile && profile.length > 0) {
        const p = profile[0];
        setName(p.name?.[0] || '');
        setBio(p.bio?.[0] || '');
        setPreferences(p.preferences?.[0] || '');
      }
    } catch (err) {
      console.error('Failed to load profile:', err);
    }
  };

  const handleConnect = () => {
    setShowConnectModal(true);
  };

  const handleSaveProfile = async () => {
    if (!agent || !isAuthenticated) {
      setError('Please connect wallet first');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Try to update first, if that fails, create new
      try {
        await updateProfile(agent, name, bio || undefined, preferences || undefined);
      } catch {
        await createProfile(agent, name, bio || undefined, preferences || undefined);
      }
      setSuccess('Profile saved successfully');
      await loadProfile();
    } catch (err: any) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSetOisyWallet = async () => {
    if (!agent || !isAuthenticated || !oisyWallet) {
      setError('Please enter OISY wallet principal');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const oisyPrincipal = Principal.fromText(oisyWallet);
      await setOisyWallet(agent, oisyPrincipal);
      setSuccess('OISY wallet set successfully');
      setOisyWalletInput('');
    } catch (err: any) {
      setError(err.message || 'Failed to set OISY wallet');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BlacksmithWorkshop>
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          <div className="glass-dark rounded-2xl p-8 mb-6">
            <h1 className="text-4xl font-bold mb-6 text-gold">User Profile</h1>
            
            {!isAuthenticated ? (
              <div className="text-center">
                <button
                  onClick={handleConnect}
                  className="bg-gold hover:bg-yellow-600 text-white font-bold py-3 px-6 rounded-lg transition-all"
                >
                  Connect Wallet
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <p className="text-gray-300 mb-2">Your Principal:</p>
                  <p className="text-sm font-mono break-all bg-black/30 p-2 rounded">
                    {principal?.toText()}
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-gray-300 mb-2">Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg bg-black/30 text-white border border-gray-600"
                      placeholder="Enter your name"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2">Bio</label>
                    <textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg bg-black/30 text-white border border-gray-600"
                      rows={4}
                      placeholder="Tell us about yourself"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2">Preferences</label>
                    <textarea
                      value={preferences}
                      onChange={(e) => setPreferences(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg bg-black/30 text-white border border-gray-600"
                      rows={3}
                      placeholder="Your preferences"
                    />
                  </div>
                </div>

                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="w-full bg-gold hover:bg-yellow-600 disabled:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition-all mb-6"
                >
                  {saving ? 'Saving...' : 'Save Profile'}
                </button>

                <div className="border-t border-gray-600 pt-6 mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-semibold text-gold">External Wallets</h2>
                    <button
                      onClick={() => setShowLinkModal(true)}
                      className="bg-gold/20 hover:bg-gold/30 text-gold font-semibold py-2 px-4 rounded-lg transition-all border border-gold/50"
                    >
                      Link Wallet
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-600 pt-6">
                  <h2 className="text-2xl font-semibold mb-4 text-gold">OISY Wallet</h2>
                  
                  <div className="mb-4">
                    <label className="block text-gray-300 mb-2">OISY Wallet Principal</label>
                    <input
                      type="text"
                      value={oisyWallet}
                      onChange={(e) => setOisyWalletInput(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg bg-black/30 text-white border border-gray-600"
                      placeholder="Enter OISY wallet principal ID"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-300 mb-2">Payout Currency</label>
                    <select
                      value={payoutCurrency}
                      onChange={(e) => setPayoutCurrency(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg bg-black/30 text-white border border-gray-600"
                    >
                      <option value="ICP">ICP</option>
                      <option value="USDC">USDC</option>
                      <option value="Bitcoin">Bitcoin</option>
                    </select>
                  </div>

                  <button
                    onClick={handleSetOisyWallet}
                    disabled={saving || !oisyWallet}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition-all"
                  >
                    {saving ? 'Setting...' : 'Set OISY Wallet'}
                  </button>
                </div>
              </>
            )}
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-4 bg-red-900/30 border border-red-500 rounded-lg text-red-200 mb-4"
            >
              {error}
            </motion.div>
          )}

          {success && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-4 bg-green-900/30 border border-green-500 rounded-lg text-green-200"
            >
              {success}
            </motion.div>
          )}
        </motion.div>

        <ConnectWalletModal
          isOpen={showConnectModal}
          onClose={() => setShowConnectModal(false)}
          showLinkWallets={false}
        />

        <ConnectWalletModal
          isOpen={showLinkModal}
          onClose={() => setShowLinkModal(false)}
          showLinkWallets={true}
        />
      </div>
    </BlacksmithWorkshop>
  );
}






